import React, { useState, useEffect, useMemo } from 'react';
import { API } from '../utils/helpers';
import useMobileViewport from '../utils/useMobileViewport';

// ── The customer's feedback page (25 Oct) ────────────────────────────────────
//
// Texted or emailed after a ticket is Done & Paid (server/helpers/feedback.js).
// One question, the standard NPS one: 0 to 10, then an optional reason. After
// answering, EVERYONE is offered the shop's Google review link, whatever the
// score: showing it only to happy customers is review gating, which Google's
// policy and the FTC's 2024 review rule forbid. A 0-6 also hears that the shop
// will be in touch. Self-contained like QuotePage: always light, mobile-first,
// the token in the URL is the credential. `&score=N` (the email's number
// buttons) only pre-selects; nothing is saved until they press Send, because
// mail scanners open links.

const BASE = {
  bg: '#f7f6f3', card: '#ffffff', border: '#e2e5ea', borderSoft: '#eef0f3',
  text: '#1a2233', sub: '#5b6472', muted: '#9aa4b2',
  accent: '#e67e22', headerBg: '#0f1724', danger: '#c0392b', ok: '#27ae60',
};
let C = { ...BASE };

export default function FeedbackPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const token = params.get('token');
  const pre = params.get('score');
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [score, setScore] = useState(pre !== null && /^(10|[0-9])$/.test(pre) ? Number(pre) : null);
  const [comment, setComment] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useMobileViewport();
  useEffect(() => {
    const html = document.documentElement;
    const prevHtml = html.style.background, prevBody = document.body.style.background;
    html.style.background = BASE.bg; document.body.style.background = BASE.bg;
    return () => { html.style.background = prevHtml; document.body.style.background = prevBody; };
  }, []);

  const take = (d) => {
    if (d.accentColor && /^#[0-9a-fA-F]{6}$/.test(d.accentColor)) C = { ...BASE, accent: d.accentColor };
    setData(d);
  };
  useEffect(() => {
    if (!token) { setLoadError(true); return; }
    fetch(`${API}/api/feedback/public/${encodeURIComponent(token)}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { take(d); if (d.answered) { setComment(d.comment || ''); if (pre === null) setScore(d.score); } })
      .catch(() => setLoadError(true));
  }, [token, pre]);

  const send = async () => {
    if (score === null) return;
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/api/feedback/public/${encodeURIComponent(token)}/answer`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ score, comment }),
      });
      const d = await r.json().catch(() => null);
      if (!d || (!r.ok && !d.shopName)) throw new Error((d && d.error) || 'failed');
      take(d); setEditing(false);
      if (d.error) setError(d.error);
      window.scrollTo(0, 0);
    } catch (_) {
      setError('That did not go through. Please try again.');
    }
    setBusy(false);
  };

  const callShop = data?.shopPhone ? ` at ${data.shopPhone}` : '';
  const shell = (children) => (
    <div style={{ minHeight: '100dvh', boxSizing: 'border-box', background: C.bg, padding: '24px 12px', fontFamily: "'Inter', -apple-system, 'Segoe UI', sans-serif" }}>
      <div style={{ maxWidth: 460, margin: '0 auto', background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,23,36,0.08)' }}>
        <div style={{ background: C.headerBg, padding: '20px 22px', textAlign: 'center' }}>
          {data?.shopLogoUrl
            ? <img src={data.shopLogoUrl} alt={(data.shopName || '').trim()} style={{ maxHeight: 56, maxWidth: 240, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
            : <div style={{ color: '#fff', fontSize: 18, fontWeight: 600 }}>{data?.shopName || 'Your bike shop'}</div>}
        </div>
        <div style={{ height: 3, background: C.accent }} />
        <div style={{ padding: 22 }}>{children}</div>
      </div>
      <div style={{ textAlign: 'center', marginTop: 14, fontSize: 11, color: C.muted }}>Powered by Spinboard</div>
    </div>
  );

  if (loadError || (data && data.state === 'not_found')) return shell(
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{ fontSize: 15, color: C.text, marginBottom: 8 }}>This link is no longer valid.</div>
      <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.6 }}>Thanks for thinking of us all the same.</div>
    </div>
  );
  if (!data) return shell(<div style={{ textAlign: 'center', padding: '40px 0', color: C.sub, fontSize: 14 }}>Loading…</div>);

  const shopName = data.shopName || 'us';
  const testBanner = data.isTest && (
    <div style={{ fontSize: 12, color: C.sub, background: C.bg, borderRadius: 8, padding: '8px 10px', marginBottom: 14, textAlign: 'center' }}>
      A test from Settings. Your answer is not counted.
    </div>
  );

  // Answered: thanks, and the Google button for everyone.
  if (data.answered && !editing) {
    const low = data.score <= 6;
    return shell(
      <>
        {testBanner}
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', margin: '0 auto 12px', fontSize: 22, lineHeight: '48px', background: '#eafaf1', color: C.ok }}>✓</div>
          <div style={{ fontSize: 17, fontWeight: 600, color: C.text }}>Thank you{data.firstName ? `, ${data.firstName}` : ''}</div>
          <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.6, marginTop: 6 }}>
            {low
              ? `We're sorry we fell short. Someone from ${shopName} will be in touch${callShop ? `, or call us${callShop}` : ''}.`
              : `It means a lot to a small shop.`}
          </div>
        </div>
        {data.google && (
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, marginBottom: 14, textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: C.text, marginBottom: 12, lineHeight: 1.5 }}>Would you share your experience on Google? It helps other riders find {shopName}.</div>
            <a href={`${API}/api/feedback/public/${encodeURIComponent(token)}/google`}
              style={{ display: 'block', padding: 14, borderRadius: 10, background: C.accent, color: '#fff', fontSize: 15, fontWeight: 600, textDecoration: 'none' }}>
              Leave us a Google review
            </a>
          </div>
        )}
        {error && <div style={{ fontSize: 13, color: C.danger, marginBottom: 10, textAlign: 'center' }}>{error}</div>}
        {data.canChange && (
          <div style={{ textAlign: 'center' }}>
            <button onClick={() => { setEditing(true); setError(''); }} style={{ background: 'none', border: 'none', color: C.sub, textDecoration: 'underline', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' }}>
              Change my answer
            </button>
          </div>
        )}
      </>
    );
  }

  const btn = (n) => {
    const on = score === n;
    return (
      <button key={n} type="button" onClick={() => setScore(n)} aria-pressed={on}
        style={{ flex: 1, minWidth: 0, padding: '13px 0', borderRadius: 8, fontSize: 16, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
          border: `1px solid ${on ? C.accent : C.border}`, background: on ? C.accent : '#fff', color: on ? '#fff' : C.text }}>{n}</button>
    );
  };
  return shell(
    <>
      {testBanner}
      <div style={{ fontSize: 15, color: C.text, marginBottom: 4 }}>Hi{data.firstName ? ` ${data.firstName}` : ''},</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: C.text, lineHeight: 1.45, marginBottom: 16 }}>
        How likely are you to recommend {shopName} to a friend?
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>{[0, 1, 2, 3, 4, 5].map(btn)}</div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>{[6, 7, 8, 9, 10].map(btn)}<span style={{ flex: 1 }} /></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: C.muted, marginBottom: 18 }}>
        <span>0 = not likely</span><span>10 = very likely</span>
      </div>
      <label style={{ display: 'block', fontSize: 13, color: C.text, marginBottom: 6 }}>What's the main reason for your score? <span style={{ color: C.muted }}>(optional)</span></label>
      <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3} maxLength={2000}
        style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 15, borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: 'inherit', resize: 'vertical', color: C.text, marginBottom: 14 }} />
      {error && <div style={{ fontSize: 13, color: C.danger, marginBottom: 10 }}>{error}</div>}
      <button onClick={send} disabled={busy || score === null}
        style={{ width: '100%', padding: 14, borderRadius: 10, border: 'none', background: C.accent, color: '#fff', fontSize: 15, fontWeight: 600, fontFamily: 'inherit', cursor: score === null ? 'default' : 'pointer', opacity: busy || score === null ? 0.5 : 1 }}>
        {busy ? 'Sending…' : 'Send'}
      </button>
    </>
  );
}
